// 工項代碼解析——2026-09-17 使用者拍板更正的模型：「規則的前綴字母（M／W）是工項代碼
// 的一部分」，「代碼一定都是從規則延伸出來的」（使用者原話）。真實工程會 XML 代碼
// （tests/fixtures/ETenderSchema/official/*.xml）：M0305042102（M前綴＋03050章碼＋
// 42102細目）、W0127116004（W前綴＋01271章碼＋16004細目）——這些章碼在 ItemCodeRule
// 資料表裡本來就存了 Prefix='M'／'W'（見 Pcces/Services/ItemCodeRuleImportService.cs
// 匯入時寫入的 Prefix 欄位）。之前把「首字母不是數字」直接當成「資源碼、沒有章碼」是
// 模型錯誤——真正該判斷的是「是不是章碼工項」，不是「CodeKind 是不是 N」。
//
// E／L 維持資源碼（真實 XML：E3511X11791／L7100203101，代碼結構跟章碼無關），另案處理，
// 這裡不動。既有 177 筆沒帶前綴字母的公版 M 工項**不做修正、不改既有列**（使用者原話
// 「舊的是填錯就不改了」）——它們之後在規則比對會被判為不符合，這是預期。
//
// ⚠⚠⚠ 跟後端 Pcces/Models/ItemCode/ItemCodeModels.cs 的 ItemCodeParser.Parse() 是
// **同構的兩份獨立實作**——前端純 JS 對後端純 C#，天生不可能共用同一份原始碼，兩邊
// 互相點名、且 tests/unit／tests/Pcces.Tests 各自讀同一份
// tests/fixtures/ItemCode/itemCodeParseSamples.json 樣本，斷言同一個結果，這是分岔時
// 唯一會變紅的東西。

/**
 * N 型（章碼工項，無前綴字母）延伸碼長度上限——2026-09-29 使用者裁示「延伸碼限制
 * 最多 2 碼，卡控一下」。延伸碼＝基本碼（章碼5碼＋細目5碼＝10碼）之後的變體尾碼
 * （例：01450A11U8 之後的 A）。⚠ 只套用在 N 型：正式庫 N 型代碼長度分布是
 * 10碼2129／11碼26／12碼10／13碼1（那 1 筆是測試資料，不是真實案例），10+2=12
 * 的上限跟既有資料不衝突。M／W 前綴型與 E／L 資源碼各自的自然長度不算延伸碼。
 * ⚠⚠⚠ 跟後端 Pcces/Models/ItemCode/ItemCodeModels.cs 的 ItemCodeParser.MaxExtensionLength
 * 是**同構的兩份獨立實作**（前端純 JS 對後端純 C#，天生不可能共用同一份原始碼），
 * 兩邊互相點名，改一邊要記得改另一邊。
 */
export const MAX_EXTENSION_LENGTH = 2

/** 依代碼首字判斷種類（跟 ProjectItemCode 現有的種類判斷同一套：數字開頭或非
 *  E/L/M/W 的其他字元一律歸 'N'）。只回答「這是哪一種規則」，不回答「有沒有章碼」——
 *  「有沒有章碼」要看 parseItemCode() 的 isChapterItem，不能假設 kind!=='N' 就代表
 *  沒有 outlineCode（M／W 就是反例）。 */
export function itemCodeKindOf(itemCode) {
  const code = String(itemCode || '')
  if (!code) return 'N'
  switch (code[0]) {
    case 'E': return 'E'
    case 'L': return 'L'
    case 'M': return 'M'
    case 'W': return 'W'
    default: return 'N'
  }
}

/**
 * 解析一串工項代碼，回傳 `{ prefix, kind, outlineCode, detail, isChapterItem }`。
 *   prefix        代碼開頭的規則前綴字母（''／'M'／'W'／'E'／'L'）。N 型（純數字開頭）
 *                  固定是空字串。
 *   kind          跟 itemCodeKindOf() 完全一致。
 *   outlineCode   章碼（5 碼）：無前綴字母（N 型）→代碼前 5 碼；M／W 前綴→代碼第
 *                 2～6 碼（跳過前綴字母那 5 碼）；E／L→null；代碼太短湊不出 5 碼時
 *                 也是 null。
 *   detail        章碼之後剩下的部分（細目碼＋可能的延伸碼，沒有再細分）；outlineCode
 *                 是 null 時，detail 是前綴字母之後的全部（E／L）或整串代碼（garbage）。
 *   isChapterItem 這個代碼是不是「掛在某個章碼底下」的工項——outlineCode 不是 null
 *                 就是 true。
 * 純函式，不驗證章碼是否真的存在於章碼清單——那是呼叫端的事。
 */
export function parseItemCode(itemCode) {
  const code = String(itemCode || '')
  const kind = itemCodeKindOf(code)

  if (kind === 'E' || kind === 'L') {
    // E／L：資源碼，代碼結構跟章碼無關（真實 XML 例：E3511X11791／L7100203101）。
    const prefix = code.length > 0 ? code[0] : ''
    const detail = code.length > 1 ? code.slice(1) : ''
    return { prefix, kind, outlineCode: null, detail, isChapterItem: false }
  }

  if (kind === 'M' || kind === 'W') {
    // M／W：前綴字母＋5碼章碼＋5碼細目。代碼太短湊不出完整章碼時 outlineCode 是
    // null（不強行湊出不完整的章碼），detail 跟著回退成「前綴之後剩下的全部」。
    const prefix = code[0]
    if (code.length < 6) return { prefix, kind, outlineCode: null, detail: code.length > 1 ? code.slice(1) : '', isChapterItem: false }
    const outlineCode = code.slice(1, 6)
    const detail = code.length > 6 ? code.slice(6) : ''
    return { prefix, kind, outlineCode, detail, isChapterItem: true }
  }

  // N 型（含真的以數字開頭、以及首字不是 E/L/M/W 的任何其他字元——itemCodeKindOf()
  // 對後者一律歸類成 'N'，這裡沿用同一個判斷，不另外挑出來當「garbage」特殊處理）。
  if (code.length < 5) return { prefix: '', kind, outlineCode: null, detail: code, isChapterItem: false }
  const outlineCode = code.slice(0, 5)
  const detail = code.length > 5 ? code.slice(5) : ''
  return { prefix: '', kind, outlineCode, detail, isChapterItem: true }
}
